'use client';

import { useEffect, useRef, useState } from 'react';
import { createClient } from '@/lib/supabase/client';
import posthog from 'posthog-js';

interface ScheduleRFQForwarder {
  id: string;
  company_name: string;
  email: string;
  rfq_email: string | null;
}

interface ScheduleRFQModalProps {
  forwarders: ScheduleRFQForwarder[];
  schedule: {
    carrier: string;
    departure_date: string | null;
    arrival_date: string | null;
    origin_unlocode: string;
    origin_name: string | null;
    destination_unlocode: string;
    destination_name: string | null;
  };
  isOpen: boolean;
  onClose: () => void;
}

const CONTAINER_TYPES = ['20ft', '40ft', '40ft HC', '45ft'] as const;
type SubmitState = 'idle' | 'submitting' | 'success_confirmed' | 'success_pending' | 'error';

export function ScheduleRFQModal({
  forwarders,
  schedule,
  isOpen,
  onClose,
}: ScheduleRFQModalProps) {
  const [containerCount, setContainerCount] = useState(1);
  const [containerType, setContainerType] = useState<(typeof CONTAINER_TYPES)[number]>('40ft');
  const [commodity, setCommodity] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [submitState, setSubmitState] = useState<SubmitState>('idle');
  const [errorMsg, setErrorMsg] = useState('');
  const autoCloseRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    const supabase = createClient();
    supabase.auth.getUser().then(({ data: { user } }) => {
      if (user?.email) {
        setUserEmail(user.email);
        setEmail(user.email);
      } else {
        setUserEmail(null);
      }
    });
  }, [isOpen]);

  useEffect(() => {
    if (submitState === 'success_confirmed' || submitState === 'success_pending') {
      autoCloseRef.current = setTimeout(() => handleClose(), 5000);
    }
    return () => {
      if (autoCloseRef.current) clearTimeout(autoCloseRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submitState]);

  function handleClose() {
    if (autoCloseRef.current) clearTimeout(autoCloseRef.current);
    setSubmitState('idle');
    setErrorMsg('');
    onClose();
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();

    if (!commodity.trim()) {
      setErrorMsg('Please describe the commodity.');
      return;
    }
    if (!email.trim()) {
      setErrorMsg('Email is required.');
      return;
    }

    setSubmitState('submitting');
    setErrorMsg('');

    try {
      const originCountry = schedule.origin_unlocode.slice(0, 2);
      const destCountry = schedule.destination_unlocode.slice(0, 2);

      const res = await fetch('/api/schedule-rfq', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          forwarder_ids: forwarders.map((f) => f.id),
          origin_port: schedule.origin_name ?? schedule.origin_unlocode,
          destination_port: schedule.destination_name ?? schedule.destination_unlocode,
          origin_country: originCountry,
          destination_country: destCountry,
          carrier: schedule.carrier,
          departure_date: schedule.departure_date,
          arrival_date: schedule.arrival_date,
          container_count: containerCount,
          container_type: containerType,
          commodity: commodity.trim(),
          requester_email: email.trim(),
          requester_name: name.trim() || undefined,
        }),
      });

      const data = await res.json();

      if (!res.ok) {
        setErrorMsg(data.error ?? 'Something went wrong. Please try again.');
        setSubmitState('error');
        return;
      }

      posthog.capture('schedule_rfq_completed', {
        carrier: schedule.carrier,
        origin: schedule.origin_unlocode,
        destination: schedule.destination_unlocode,
        container_count: containerCount,
        container_type: containerType,
        forwarder_count: forwarders.length,
        email_confirmed: data.email_confirmed,
      });

      setSubmitState(data.email_confirmed ? 'success_confirmed' : 'success_pending');
    } catch {
      setErrorMsg('Network error. Please check your connection and try again.');
      setSubmitState('error');
    }
  }

  if (!isOpen) return null;

  const originLabel = schedule.origin_name ?? schedule.origin_unlocode;
  const destLabel = schedule.destination_name ?? schedule.destination_unlocode;

  return (
    <div className="srfq-overlay" onClick={(e) => { if (e.target === e.currentTarget) handleClose(); }}>
      <div className="srfq-modal" role="dialog" aria-modal="true" aria-label="Request a quote for this sailing">
        {/* Header */}
        <div className="srfq-header">
          <div className="srfq-header-title">Get a Quote</div>
          <div className="srfq-header-sub">
            {originLabel} → {destLabel} · {schedule.carrier}
          </div>
          <button className="srfq-close" onClick={handleClose} aria-label="Close">×</button>
        </div>

        {/* Success state */}
        {(submitState === 'success_confirmed' || submitState === 'success_pending') && (
          <div className="srfq-success">
            {submitState === 'success_confirmed' ? (
              <>
                <div className="srfq-success-icon">✓</div>
                <div className="srfq-success-title">Request Sent</div>
                <div className="srfq-success-msg">
                  Your quote request has been sent to{' '}
                  <strong>{forwarders.map((f) => f.company_name).join(', ')}</strong>.
                  They&apos;ll respond to your email shortly.
                </div>
              </>
            ) : (
              <>
                <div className="srfq-success-icon">✉</div>
                <div className="srfq-success-title">Check Your Inbox</div>
                <div className="srfq-success-msg">
                  We sent a confirmation email to <strong>{email}</strong>.
                  Click the link to send your request to the forwarders.
                </div>
              </>
            )}
            <button className="srfq-close-btn" onClick={handleClose}>Close</button>
          </div>
        )}

        {/* Form */}
        {submitState !== 'success_confirmed' && submitState !== 'success_pending' && (
          <form className="srfq-form" onSubmit={handleSubmit} noValidate>
            <div className="srfq-section-label">Sailing Details</div>
            <div className="srfq-route-info">
              <span>{originLabel}</span>
              <span className="srfq-arrow">→</span>
              <span>{destLabel}</span>
            </div>
            <div className="srfq-meta-info">
              {schedule.carrier}
              {schedule.departure_date && ` · Dep. ${schedule.departure_date}`}
              {schedule.arrival_date && ` · Arr. ${schedule.arrival_date}`}
            </div>

            <div className="srfq-section-label" style={{ marginTop: 20 }}>Cargo Information</div>

            <div className="srfq-row">
              <div className="srfq-field srfq-field--half">
                <label className="srfq-label" htmlFor="srfq-count">Containers</label>
                <input
                  id="srfq-count"
                  className="srfq-input"
                  type="number"
                  min={1}
                  max={999}
                  value={containerCount}
                  onChange={(e) => setContainerCount(Math.max(1, parseInt(e.target.value) || 1))}
                />
              </div>
              <div className="srfq-field srfq-field--half">
                <label className="srfq-label" htmlFor="srfq-type">Container Type</label>
                <select
                  id="srfq-type"
                  className="srfq-input srfq-select"
                  value={containerType}
                  onChange={(e) => setContainerType(e.target.value as typeof containerType)}
                >
                  {CONTAINER_TYPES.map((t) => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="srfq-field">
              <label className="srfq-label" htmlFor="srfq-commodity">Commodity</label>
              <input
                id="srfq-commodity"
                className="srfq-input"
                type="text"
                placeholder="e.g. Electronics, Furniture, Garments"
                value={commodity}
                onChange={(e) => setCommodity(e.target.value)}
                required
              />
            </div>

            <div className="srfq-section-label" style={{ marginTop: 20 }}>Your Details</div>

            <div className="srfq-field">
              <label className="srfq-label" htmlFor="srfq-name">Your Name</label>
              <input
                id="srfq-name"
                className="srfq-input"
                type="text"
                placeholder="Full name"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>

            {!userEmail && (
              <>
                <div className="srfq-field">
                  <label className="srfq-label" htmlFor="srfq-email">Your Email</label>
                  <input
                    id="srfq-email"
                    className="srfq-input"
                    type="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </div>
                <div className="srfq-note">
                  We&apos;ll send a confirmation email before forwarding your request.
                </div>
              </>
            )}

            {userEmail && (
              <div className="srfq-note srfq-note--auth">
                Sending as <strong>{userEmail}</strong>
              </div>
            )}

            <div className="srfq-forwarder-list">
              <div className="srfq-section-label">Sending to</div>
              {forwarders.map((f) => (
                <div key={f.id} className="srfq-forwarder-chip">{f.company_name}</div>
              ))}
            </div>

            {submitState === 'error' && (
              <div className="srfq-error">{errorMsg}</div>
            )}

            <button
              className="srfq-submit"
              type="submit"
              disabled={submitState === 'submitting'}
            >
              {submitState === 'submitting' ? 'Sending…' : 'Send Quote Request'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
